import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Product } from '@/types';
import { useLanguage } from '@/contexts/LanguageContext';

interface PromotionCarouselProps {
  promotions: Product[];
}

export function PromotionCarousel({ promotions }: PromotionCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { hotelId } = useParams<{ hotelId: string }>();
  
  useEffect(() => {
    if (promotions.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === promotions.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);
    
    return () => clearInterval(interval);
  }, [promotions.length]);
  
  if (promotions.length === 0) {
    return null;
  }
  
  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? promotions.length - 1 : prevIndex - 1
    );
  };
  
  const handleNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === promotions.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const handlePromotionClick = () => {
    const promotion = promotions[currentIndex];
    navigate(`/${hotelId}/product/${promotion.id}`);
  };
  
  return (
    <div className="relative mb-4">
      <div className="overflow-hidden rounded-lg">
        <div className="relative aspect-[2/1] w-full">
          <img
            src={promotions[currentIndex].image}
            alt={promotions[currentIndex].name}
            className="h-full w-full object-cover"
          />
          
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex flex-col justify-end p-8">
            <h3 className="text-white font-bold text-lg">
              {promotions[currentIndex].name}
            </h3>
            <p className="text-white/90 text-sm">
              {promotions[currentIndex].description}
            </p>
            
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2 bg-white/20 text-white border-white/40 backdrop-blur-sm w-fit"
              onClick={handlePromotionClick}
            >
              {t('viewOffer')}
            </Button>
          </div>
        </div>
      </div>
      
      {promotions.length > 1 && (
        <>
          <Button
            variant="outline"
            size="icon"
            className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-white/70 border-none"
            onClick={handlePrevious}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          
          <Button
            variant="outline"
            size="icon"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-white/70 border-none"
            onClick={handleNext}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {promotions.map((_, index) => (
              <div
                key={index}
                className={`h-1.5 rounded-full ${
                  index === currentIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/50'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}